/**
 * Diameter measurement computation.
 *
 * Handles:
 * - Diameter at a keypoint (nearest node)
 * - Offset diameter (fixed distance from keypoint along centerline)
 * - Min/max diameter over a range of nodes
 *
 * All diameter lookups respect manual edits stored in the DiameterEdit map.
 */

import type { Centerline, CenterlineNode } from '../../centerline';
import type { DiameterEdit } from '../diameter-edit';
import { findPathContainingNode, findPathContainingNodes } from './path-resolver';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get the effective diameter value at a node, applying any manual edit.
 */
function getEffectiveDiameter(
    node: CenterlineNode,
    diameterEdits: ReadonlyMap<number, DiameterEdit>
): { value: number; isEdited: boolean } {
    const edit = diameterEdits.get(node.id);
    if (edit) {
        return { value: edit.editedValue, isEdited: edit.isModified };
    }
    return { value: node.diameter.value, isEdited: false };
}

// ============================================================================
// Diameter at node
// ============================================================================

export interface DiameterResult {
    readonly value: number;
    readonly nodeId: number;
    readonly isEdited: boolean;
}

/**
 * Compute diameter at a specific node.
 */
export function computeDiameterAtNode(
    nodeId: number,
    nodePool: ReadonlyMap<number, CenterlineNode>,
    diameterEdits: ReadonlyMap<number, DiameterEdit>
): DiameterResult | null {
    const node = nodePool.get(nodeId);
    if (!node) return null;

    const { value, isEdited } = getEffectiveDiameter(node, diameterEdits);
    return { value, nodeId, isEdited };
}

// ============================================================================
// Offset diameter
// ============================================================================

export interface OffsetDiameterResult {
    readonly value: number;
    readonly nodeId: number;
    readonly isEdited: boolean;
    readonly keypointId: string;
    readonly offsetMm: number;
}

/**
 * Compute diameter at a fixed offset distance from a keypoint node.
 *
 * Positive offset = distal (toward higher node indexes / bifurcation).
 * Negative offset = proximal (toward lower node indexes / aorta start).
 */
export function computeOffsetDiameter(
    keypointNodeId: number,
    keypointId: string,
    offsetMm: number,
    centerlines: ReadonlyMap<string, Centerline>,
    diameterEdits: ReadonlyMap<number, DiameterEdit>
): OffsetDiameterResult | null {
    const resolved = findPathContainingNode(keypointNodeId, centerlines);
    if (!resolved) return null;

    const { centerline, index: keypointIndex } = resolved;
    const targetIndex = centerline.getIndexAtDistance(offsetMm, keypointIndex);
    const targetNode = centerline.getNodeAt(targetIndex);

    const { value, isEdited } = getEffectiveDiameter(targetNode, diameterEdits);
    return { value, nodeId: targetNode.id, isEdited, keypointId, offsetMm };
}

// ============================================================================
// Min/max diameter
// ============================================================================

export interface MinMaxDiameterResult {
    readonly value: number;
    /** Node ID where the min/max occurs */
    readonly nodeId: number;
    readonly isEdited: boolean;
    readonly fromNodeId: number;
    readonly toNodeId: number;
}

/**
 * Compute min or max diameter over a range of centerline nodes between two node IDs.
 */
export function computeMinMaxDiameter(
    fromNodeId: number,
    toNodeId: number,
    mode: 'min' | 'max',
    centerlines: ReadonlyMap<string, Centerline>,
    diameterEdits: ReadonlyMap<number, DiameterEdit>
): MinMaxDiameterResult | null {
    const resolved = findPathContainingNodes(fromNodeId, toNodeId, centerlines);
    if (!resolved) return null;

    const { centerline, indexA, indexB } = resolved;
    const startIdx = Math.min(indexA, indexB);
    const endIdx = Math.max(indexA, indexB);

    let bestValue = mode === 'min' ? Infinity : -Infinity;
    let bestNodeId = centerline.nodes[startIdx].id;
    let bestIsEdited = false;

    for (let i = startIdx; i <= endIdx; i++) {
        const node = centerline.nodes[i];
        const { value, isEdited } = getEffectiveDiameter(node, diameterEdits);

        const isBetter = mode === 'min' ? value < bestValue : value > bestValue;
        if (isBetter) {
            bestValue = value;
            bestNodeId = node.id;
            bestIsEdited = isEdited;
        }
    }

    if (!isFinite(bestValue)) return null;

    return {
        value: bestValue,
        nodeId: bestNodeId,
        isEdited: bestIsEdited,
        fromNodeId,
        toNodeId,
    };
}
